/**
 * Quantity Buy Box Utils
 * Handles add to cart, remove from cart, and product card interactions
 */

(function() {
  const BUYO_CLASS = 'buyo';

  // Get translation from config element if available
  const configEl = document.getElementById('buyo-config');
  const REMOVE_TEXT = configEl ? configEl.dataset.removeText : 'Remove';

  async function removeFromCart(cartItemKey, productCardElement, quantity) {
    try {
      const selectedVariant = productCardElement.querySelector('.variant-container .opt-btn:checked');
      const variantId = selectedVariant ? parseInt(selectedVariant.dataset.variantId) : null;

      // Use centralized cart API with queue management
      let cartData;
      if (window.MarketCartUtils && window.MarketCartUtils.changeCartItem) {
        cartData = await window.MarketCartUtils.changeCartItem(cartItemKey, 0);
      } else {
        const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: cartItemKey,
            quantity: 0
          })
        });

        if (!response.ok) {
          throw new Error('Failed to remove item from cart');
        }

        cartData = await response.json();
      }

      // Update button back to original state
      const buyButtons = productCardElement.querySelectorAll('.buyo_option');
      buyButtons.forEach(btn => {
        if (btn.dataset.q === String(quantity)) {
          const btnEl = btn.querySelector('.buyo_btn');
          if (btnEl && btnEl.dataset.originalText) {
            const textSpan = btnEl.querySelector('.buyo_btn-text');
            if (textSpan) textSpan.textContent = btnEl.dataset.originalText; else btnEl.textContent = btnEl.dataset.originalText;
            btnEl.removeAttribute('data-cart-item-key');
            btnEl.removeAttribute('data-original-text');
            btn.classList.remove('is-in-cart');
          }
        }
      });

      // Dispatch on:line-item:change event
      if (variantId && cartData.items !== undefined) {
        document.dispatchEvent(new CustomEvent('on:line-item:change', {
          bubbles: true,
          detail: {
            cart: cartData,
            variantId: variantId,
            oldQuantity: 1,
            newQuantity: 0
          }
        }));
      }

      // Dispatch cart events
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartData }
      }));

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cartData }
      }));

      // Update MarketCartUtils event cache if available
      if (window.MarketCartUtils && window.MarketCartUtils.setEventCartData) {
        window.MarketCartUtils.setEventCartData(cartData);
      }

      // Update cart drawer and cart icon
      const cartDrawer = document.querySelector('cart-drawer');
      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        const cartDrawerSection = cartDrawer.closest('.shopify-section');
        if (cartDrawerSection) {
          sections += ',' + cartDrawerSection.id.replace('shopify-section-', '');
        }
      }

      // Fetch and update sections
      try {
        let sectionsUrl = window.Shopify.routes.root + '?sections=' + sections;
        if (window.MarketCartUtils && window.MarketCartUtils.getMarketAwareCartUrl) {
          sectionsUrl = window.MarketCartUtils.getMarketAwareCartUrl('?sections=' + sections);
        }

        const sectionsResponse = await fetch(sectionsUrl);
        if (sectionsResponse.ok) {
          const sectionsData = await sectionsResponse.json();

          if (sectionsData.sections && sectionsData.sections['cart-icon-bubble']) {
            if (window.MarketCartUtils && window.MarketCartUtils.updateCartIconBubble) {
              window.MarketCartUtils.updateCartIconBubble(sectionsData.sections['cart-icon-bubble']);
            }
          }

          if (cartDrawer && sectionsData.sections) {
            const drawerSectionId = cartDrawer.closest('.shopify-section')?.id.replace('shopify-section-', '');
            if (drawerSectionId && sectionsData.sections[drawerSectionId]) {
              document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
                bubbles: true,
                detail: { cart: cartData }
              }));

              if (typeof cartDrawer.refresh === 'function') {
                cartDrawer.refresh(true);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error updating cart sections:', error);
      }

      // Update cart-items component if on cart page
      const cartItems = document.querySelector('cart-items');
      if (cartItems && typeof cartItems.refresh === 'function') {
        cartItems.refresh();
      }

    } catch (error) {
      console.error('Error removing item from cart:', error);
    }
  }

  /**
   * Show a short inline notice under a buy box option (used for the inventory
   * guard, which blocks the add rather than silently overselling).
   */
  function showBuyoNotice(buyoOption, message) {
    if (!buyoOption) return;
    let notice = buyoOption.parentElement.querySelector(`.${BUYO_CLASS}_notice`);
    if (!notice) {
      notice = document.createElement('div');
      notice.className = `${BUYO_CLASS}_notice`;
      notice.setAttribute('role', 'status');
      notice.style.cssText = 'margin-top:6px;font-size:12px;line-height:1.3;color:#b42318;text-align:center;';
      buyoOption.insertAdjacentElement('afterend', notice);
    }
    notice.textContent = message;
    clearTimeout(notice.hideTimer);
    notice.hideTimer = setTimeout(() => notice.remove(), 5000);
  }

  async function addProdToCart(prodId, quantity, productCardElement, inventoryAvailable) {
    // Preload cart-drawer scripts in parallel with the add request so
    // `cartDrawer.renderContents` is defined by the time we call it on first
    // add (cart-drawer.js is lazy-loaded and not guaranteed to be registered
    // before this runs).
    const cartScriptsReady = window.loadCartDrawerScripts
      ? window.loadCartDrawerScripts()
      : Promise.resolve();
    try {
      // NOTE: we intentionally do NOT bail out when MarketCartUtils.isCartBusy()
      // is true. isCartBusy() reports true while ANY mutation is queued/in-flight,
      // so bailing here silently dropped legitimate rapid adds of *different*
      // product cards (the button still showed its spinner + "active" state, so it
      // looked added but wasn't). Rapid duplicate clicks are already guarded two
      // other ways: the per-button spinner + pointerEvents lock in the click
      // handler blocks repeat clicks on the same button, and addToCart()'s
      // pendingAddOperations dedupes identical in-flight operations. So we let the
      // add through and MarketCartUtils.addToCart() serializes it via its queue.

      // Check if this is a quantity_restricted product and if item is already in cart
      if (productCardElement && productCardElement.dataset.quantityRestricted === 'true') {
        // Use cached cart data instead of fetching
        let checkCartData;
        if (window.MarketCartUtils) {
          checkCartData = window.MarketCartUtils.getEventCartData();
          if (!checkCartData) {
            checkCartData = await window.MarketCartUtils.getMarketAwareCartData();
          }
        } else {
          const checkCartResponse = await fetch('/cart.js');
          if (checkCartResponse.ok) {
            checkCartData = await checkCartResponse.json();
          }
        }

        if (checkCartData) {
          const isAlreadyInCart = checkCartData.items.some(item => {
            if (item.variant_id !== parseInt(prodId) || !item.properties) return false;
            const isQuantityRestricted = item.properties._quantity_restricted === 'true';
            const isUpsell = item.properties._upsell_item === 'true';
            const isCheckoutBlockUpsell = item.properties._checkoutblocks && String(item.properties._checkoutblocks).includes('advanced-upsell');
            return isQuantityRestricted || isUpsell || isCheckoutBlockUpsell;
          });

          if (isAlreadyInCart) {
            return;
          }
        }
      }

      if (typeof SLIDECART_UPDATE === "function") SLIDECART_UPDATE();

      const cartDrawer = document.querySelector('cart-drawer');
      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // Build properties object
      const properties = {};
      if (productCardElement && productCardElement.dataset.quantityRestricted === 'true') {
        properties._quantity_restricted = 'true';
        properties._restricted_id = prodId;
      }

      // Optimistic badge bump + cart icon shake are handled centrally by
      // MarketCartUtils.addToCart (see market-cart-utils.js).

      // Use centralized cart API with queue management
      let data;
      if (window.MarketCartUtils && window.MarketCartUtils.addToCart) {
        data = await window.MarketCartUtils.addToCart(prodId, quantity, properties, {
          sections: sections,
          sections_url: window.location.pathname + window.location.search,
          inventoryAvailable: inventoryAvailable
        });
      } else {
        // Fallback to direct fetch
        const formData = new FormData();
        formData.append('id', prodId);
        formData.append('quantity', quantity);
        formData.append('sections_url', window.location.pathname + window.location.search);
        formData.append('sections', sections);

        if (productCardElement && productCardElement.dataset.quantityRestricted === 'true') {
          formData.append('properties[_quantity_restricted]', 'true');
          formData.append('properties[_restricted_id]', prodId);
        }

        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        // Safe JSON parsing
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('text/html')) {
          throw new Error(response.status === 429 ? 'Too many requests. Please wait.' : 'Server error');
        }

        data = await response.json();

        if (!response.ok || data.status) {
          let error = typeof data.description === 'string' ? data.description : data.message;
          if (data.errors && typeof data.errors === 'object') {
            error = Object.entries(data.errors).map((item) => item[1].join(', '));
          }
          throw new Error(error);
        }
      }

      // Handle errors from centralized API
      if (data && data.status && data.status !== 200) {
        let error = typeof data.description === 'string' ? data.description : data.message;
        throw new Error(error || 'Failed to add to cart');
      }

      if (data.sections && data.sections['cart-icon-bubble']) {
        if (window.MarketCartUtils && window.MarketCartUtils.updateCartIconBubble) {
          window.MarketCartUtils.updateCartIconBubble(data.sections['cart-icon-bubble']);
        }
      }

      // Fetch fresh cart data and dispatch events for other components.
      // Fire-and-forget: do NOT block the button on this GET. It only feeds
      // events (and the quantity-restricted "Remove" relabel) for other
      // components. Under Shopify rate-limiting on rapid adds, awaiting it kept
      // the buy button spinning long after the item was already in the cart.
      (async () => {
      try {
        let cartUrl = '/cart.js';
        if (window.MarketCartUtils && window.MarketCartUtils.getMarketAwareCartUrl) {
          cartUrl = window.MarketCartUtils.getMarketAwareCartUrl('/cart.js');
        }

        const cartResponse = await fetch(cartUrl);
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();

          if (productCardElement && productCardElement.dataset.quantityRestricted === 'true') {
            const cartItem = cartData.items.find(item => {
              if (item.variant_id !== parseInt(prodId) || !item.properties) return false;
              const isQuantityRestricted = item.properties._quantity_restricted === 'true';
              const isUpsell = item.properties._upsell_item === 'true';
              const isCheckoutBlockUpsell = item.properties._checkoutblocks && String(item.properties._checkoutblocks).includes('advanced-upsell');
              return isQuantityRestricted || isUpsell || isCheckoutBlockUpsell;
            });

            if (cartItem) {
              const buyButtons = productCardElement.querySelectorAll('.buyo_option');
              buyButtons.forEach(btn => {
                if (btn.dataset.q === String(quantity)) {
                  const btnEl = btn.querySelector('.buyo_btn');
                  if (btnEl) {
                    const textSpan = btnEl.querySelector('.buyo_btn-text');
                    btnEl.dataset.originalText = (textSpan || btnEl).textContent.trim();
                    btnEl.dataset.cartItemKey = cartItem.key;
                    if (textSpan) textSpan.textContent = REMOVE_TEXT; else btnEl.textContent = REMOVE_TEXT;
                    btn.classList.add('is-in-cart');
                  }
                }
              });
            }
          }

          document.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: cartData,
              variantId: prodId
            }
          }));

          document.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: cartData }
          }));

          document.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { cart: cartData }
          }));

          if (window.MarketCartUtils && window.MarketCartUtils.setEventCartData) {
            window.MarketCartUtils.setEventCartData(cartData);
          }
        }
      } catch (error) {
        console.error('Error fetching cart data after add:', error);
      }
      })();

      document.dispatchEvent(new CustomEvent('custom:cart:updated', {
        bubbles: true
      }));

      if (cartDrawer) {
        const hasOpenedCart = sessionStorage.getItem('plp_cart_opened');
        const shouldOpenDrawer = !hasOpenedCart;
        if (shouldOpenDrawer) {
          sessionStorage.setItem('plp_cart_opened', '1');
        }
        const scheduleUpdate = window.scheduleCartDrawerAfterAdd || ((drawer, payload, opts) => {
          return drawer.renderContents(payload, opts.openDrawer);
        });
        cartScriptsReady
          .then(() => scheduleUpdate(cartDrawer, data, { openDrawer: shouldOpenDrawer }))
          .catch(err => console.error('Error rendering cart drawer:', err));
      }

    } catch (error) {
      console.error('Error adding product to cart:', error);
      throw error;
    }
  }

  function getAddToCartErrorMessage(error) {
    if (window.MarketCartUtils
      && typeof window.MarketCartUtils.getCustomerFacingCartError === 'function') {
      return window.MarketCartUtils.getCustomerFacingCartError(error) || error.message;
    }
    return error?.message || 'Failed to add to cart';
  }

  function dispatchCartWideAddError(error, displayMessage) {
    const message = displayMessage || getAddToCartErrorMessage(error);
    document.dispatchEvent(new CustomEvent('on:cart:error', {
      bubbles: true,
      detail: { error: message },
    }));
  }

  function removeClasses(el, names) {
    names.forEach(name => el.closest('product-card').querySelectorAll(`.${BUYO_CLASS}_option.${name}`).forEach(el => el.classList.remove(name)));
  }

  // Extract sorted tier quantities from a price mapping string
  function getTierQtys(priceMapping) {
    if (!priceMapping) return [1];
    return priceMapping.split(',').map(entry => parseInt(entry.split(':')[0])).sort((a, b) => a - b);
  }

  function getStartpointQty(priceMapping) {
    if (!priceMapping) return null;
    const entries = priceMapping.split(',');
    for (const entry of entries) {
      const parts = entry.split(':');
      if (parts[3] === '1') return parseInt(parts[0]);
    }
    return null;
  }

  function getPrices(priceMapping, quantity) {
    const prices = priceMapping.split(',').reduce((acc, item) => {
      const parts = item.split(':');
      acc[parts[0]] = {
        price: Number(parts[1]).toFixed(2),
        comparePrice: Number(parts[2]).toFixed(2)
      };
      return acc;
    }, {});

    const quantities = Object.keys(prices).map(Number).sort((a, b) => a - b);

    // Use the highest tier at or below the requested quantity,
    // falling back to the lowest tier only if quantity is below all tiers
    let qty = quantities[0];
    for (const q of quantities) {
      if (q <= quantity) qty = q;
      else break;
    }

    return prices[qty];
  }

  async function updateQuantityRestrictedButtons(eventCartData = null) {
    try {
      // Use cart data from event if provided, otherwise use cache
      let cartData = eventCartData;

      if (!cartData) {
        if (window.MarketCartUtils) {
          // First try event cache (no fetch needed)
          cartData = window.MarketCartUtils.getEventCartData();
          if (!cartData) {
            // Fall back to cached fetch with throttling
            cartData = await window.MarketCartUtils.getMarketAwareCartData();
          }
        } else {
          const cartResponse = await fetch('/cart.js');
          if (!cartResponse.ok) return;
          cartData = await cartResponse.json();
        }
      }

      if (!cartData || !cartData.items) return;

      document.querySelectorAll('product-card[data-quantity-restricted="true"]').forEach(productCard => {
        const selectedVariant = productCard.querySelector('.variant-container .opt-btn:checked');
        if (!selectedVariant) return;

        const variantId = parseInt(selectedVariant.dataset.variantId);

        const buyButtons = productCard.querySelectorAll('.buyo_option');
        buyButtons.forEach(btn => {
          const btnEl = btn.querySelector('.buyo_btn');
          if (btnEl && btnEl.dataset.originalText) {
            const textSpan = btnEl.querySelector('.buyo_btn-text');
            if (textSpan) textSpan.textContent = btnEl.dataset.originalText; else btnEl.textContent = btnEl.dataset.originalText;
            btnEl.removeAttribute('data-cart-item-key');
            btnEl.removeAttribute('data-original-text');
            btn.classList.remove('is-in-cart');
          }
        });

        cartData.items.forEach(cartItem => {
          if (cartItem.variant_id !== variantId || !cartItem.properties) return;

          const isQuantityRestricted = cartItem.properties._quantity_restricted === 'true';
          const isUpsell = cartItem.properties._upsell_item === 'true';
          const isCheckoutBlockUpsell = cartItem.properties._checkoutblocks && String(cartItem.properties._checkoutblocks).includes('advanced-upsell');

          if (isQuantityRestricted || isUpsell || isCheckoutBlockUpsell) {
            buyButtons.forEach(btn => {
              if (parseInt(btn.dataset.q) === cartItem.quantity) {
                const btnEl = btn.querySelector('.buyo_btn');
                if (btnEl) {
                  if (!btnEl.dataset.originalText) {
                    const textSpan = btnEl.querySelector('.buyo_btn-text');
                    btnEl.dataset.originalText = (textSpan || btnEl).textContent.trim();
                  }
                  btnEl.dataset.cartItemKey = cartItem.key;
                  const textSpan = btnEl.querySelector('.buyo_btn-text');
                  if (textSpan) textSpan.textContent = REMOVE_TEXT; else btnEl.textContent = REMOVE_TEXT;
                  btn.classList.add('is-in-cart');
                }
              }
            });
          }
        });
      });
    } catch (error) {
      console.error('Error updating quantity restricted buttons:', error);
    }
  }

  window.buyo_init = function() {
    updateQuantityRestrictedButtons();

    // When we change product card variant
    document.querySelectorAll('product-card .variant-container .opt-btn').forEach(el => {
      // Bind once — buyo_init() re-runs and would otherwise stack listeners.
      if (el.dataset.buyoVariantBound === '1') return;
      el.dataset.buyoVariantBound = '1';
      el.addEventListener("click", () => {
        removeClasses(el, ['active', 'unavailable', 'soldout10']);
        var varId = el.dataset.variantId;
        var productCard = el.closest('product-card');
        // Clear sold out state from buyo options when switching variants
        productCard.querySelectorAll(`.${BUYO_CLASS}_option`).forEach(opt => opt.classList.remove('unavailable', 'soldout10'));
        var productCardSlider = productCard.querySelector('product-card-image-slider');
        var linkElement = productCard.querySelector('a');
        var url = new URL(linkElement.href);

        url.searchParams.set('variant', varId);

        const priceMapping = el.dataset.pricePerQ;
        const startpointQty = getStartpointQty(priceMapping);

        productCard.querySelectorAll('.buyo_option').forEach(option => {
          // Reset to startpoint qty if available
          if (startpointQty) {
            option.dataset.q = startpointQty;
            var numEl = option.querySelector(`.${BUYO_CLASS}_number`);
            if (numEl) numEl.textContent = 'x' + startpointQty;
          }

          const quantity = parseInt(option.dataset.q);
          const { price, comparePrice } = getPrices(priceMapping, quantity);
          const unitPrice = price / quantity;

          // Update per-pack price in left area
          const priceEl = option.querySelector('.buyo_price');
          if (priceEl && priceEl.querySelector('b')) priceEl.querySelector('b').textContent = formatMoney(unitPrice * 100, window.money_format);
          if (priceEl && priceEl.querySelector('i')) priceEl.querySelector('i').textContent = formatMoney(comparePrice * 100, window.money_format);

          // Update total price on button
          const btnEl = option.querySelector('.buyo_btn');
          if (btnEl && btnEl.querySelector('b')) btnEl.querySelector('b').textContent = formatMoney(price * 100, window.money_format);
        });

        if (productCardSlider) {
          setTimeout(() => {
            productCardSlider.querySelectorAll('.slider__item').forEach(el => {
              var linkElement = el.querySelector('a');
              linkElement.href = url.toString();
            });
          }, 200);
        } else {
          linkElement.href = url.toString();
        }

        var inventory = parseInt(el.dataset.inventory);

        if (inventory < 1) {
          el.closest('product-card').querySelectorAll(`.${BUYO_CLASS}_option`).forEach(el => el.classList.add('unavailable'));
        } else {
          // Mark tiers that exceed inventory as sold out
          const cardTierQtys = getTierQtys(priceMapping);
          cardTierQtys.forEach(q => {
            if (q > inventory) {
              const tierEl = el.closest('product-card').querySelector(`[data-q="${q}"]`);
              if (tierEl) tierEl.classList.add('soldout10');
            }
          });
        }

        // If selected qty exceeds new variant inventory, deselect popup radio and reset buyo to qty 1
        var popup = productCard.querySelector(`.${BUYO_CLASS}_popup`);
        if (popup) {
          // Deselect all radios and selected rows
          popup.querySelectorAll(`.${BUYO_CLASS}_popup__row`).forEach(r => r.classList.remove('is-selected'));
          popup.querySelectorAll(`.${BUYO_CLASS}_popup__radio`).forEach(r => r.checked = false);
          // Close popup and overlay on variant change
          popup.style.display = 'none';
          const overlayVariant = popup.previousElementSibling;
          if (overlayVariant && overlayVariant.classList.contains(`${BUYO_CLASS}_popup__overlay`)) overlayVariant.style.display = 'none';
        }

        // Reset buyo options: if current qty > inventory, step down to the highest available qty
        const variantTierQtys = getTierQtys(priceMapping);
        productCard.querySelectorAll(`.${BUYO_CLASS}_option`).forEach(option => {
          var currentQ = parseInt(option.dataset.q) || 1;
          if (currentQ > inventory) {
            var newQ = variantTierQtys[0] || 1;
            for (const q of variantTierQtys) {
              if (q <= inventory) newQ = q;
              else break;
            }
            option.dataset.q = newQ;
            var numEl = option.querySelector(`.${BUYO_CLASS}_number`);
            if (numEl) numEl.textContent = 'x' + newQ;
            var { price, comparePrice } = getPrices(priceMapping, newQ);
            var unitPrice = price / newQ;
            var priceEl = option.querySelector(`.${BUYO_CLASS}_price`);
            if (priceEl && priceEl.querySelector('b')) priceEl.querySelector('b').textContent = formatMoney(unitPrice * 100, window.money_format);
            if (priceEl && priceEl.querySelector('i')) priceEl.querySelector('i').textContent = formatMoney(comparePrice * 100, window.money_format);
            var btnEl = option.querySelector(`.${BUYO_CLASS}_btn`);
            if (btnEl && btnEl.querySelector('b')) btnEl.querySelector('b').textContent = formatMoney(price * 100, window.money_format);
          }
        });

        if (productCard.dataset.quantityRestricted === 'true') {
          updateQuantityRestrictedButtons();
        }
      });
    });

    // When we click on the buy button (right side only)
    document.querySelectorAll(`.${BUYO_CLASS}_option .${BUYO_CLASS}_btn`).forEach(btnEl => {
      // buyo_init() runs multiple times (DOMContentLoaded, facet updates,
      // deferred-section:activated, pagination, recommendations…). Without this
      // guard each run stacks ANOTHER click listener on the same button, so a
      // single user click fires addProdToCart several times — causing
      // duplicate/racing adds AND a stuck spinner (the 2nd stacked handler
      // captures the 1st handler's freshly-injected spinner as its savedHTML and
      // restores the button to that spinner after the tick). Bind once per button.
      if (btnEl.dataset.buyoBtnBound === '1') return;
      btnEl.dataset.buyoBtnBound = '1';
      btnEl.addEventListener("click", async (e) => {
        e.stopPropagation();
        const el = btnEl.closest(`.${BUYO_CLASS}_option`);
        if (['unavailable', 'soldout10'].some(className => el.classList.contains(className))) return;

        const productCard = el.closest('product-card');

        if (el.classList.contains('is-in-cart') && btnEl.dataset.cartItemKey) {
          removeFromCart(btnEl.dataset.cartItemKey, productCard, el.dataset.q);
          return;
        }

        removeClasses(el, ['active', 'unavailable']);
        const savedHTML = btnEl.innerHTML;
        btnEl.innerHTML = '<span class="buyo_btn-spinner" style="display:inline-block;"></span>';
        btnEl.style.pointerEvents = 'none';
        const checkedVariant = productCard.querySelector('.variant-container .opt-btn:checked');
        const variant_id = checkedVariant.dataset.variantId;
        // data-inventory is only a hard limit when the variant denies overselling;
        // otherwise leave it to the server so backorders still work.
        const inventoryAvailable = checkedVariant.dataset.inventoryTracked === 'true'
          ? parseInt(checkedVariant.dataset.inventory, 10)
          : null;
        let added = true;
        try {
          await addProdToCart(variant_id, el.dataset.q, productCard, inventoryAvailable);
          await new Promise(r => setTimeout(r, 600));
        } catch (err) {
          console.error(err);
          added = false;
          const message = getAddToCartErrorMessage(err);
          showBuyoNotice(el, message);
          dispatchCartWideAddError(err, message);
        }
        btnEl.innerHTML = savedHTML;
        btnEl.style.pointerEvents = '';
        if (!added) return;
        el.classList.add("active");
        setTimeout(() => {
          el.classList.remove("active");
        }, 3000);
      });
    });

    // Buyo left-click popup
    document.querySelectorAll(`.${BUYO_CLASS}_option .${BUYO_CLASS}_left`).forEach(leftEl => {
      // Bind once — buyo_init() re-runs and would otherwise stack listeners,
      // toggling the popup open then immediately closed on a single click.
      if (leftEl.dataset.buyoLeftBound === '1') return;
      leftEl.dataset.buyoLeftBound = '1';
      leftEl.addEventListener('click', (e) => {
        e.stopPropagation();
        const buyoOption = leftEl.closest(`.${BUYO_CLASS}_option`);
        if (buyoOption.classList.contains('unavailable')) return;
        const productCard = buyoOption.closest('product-card');
        const popup = productCard.querySelector(`.${BUYO_CLASS}_popup`);
        if (!popup) return;

        // Close any other open popups and their overlays
        document.querySelectorAll(`.${BUYO_CLASS}_popup`).forEach(p => {
          if (p !== popup) {
            p.style.display = 'none';
            const overlay = p.previousElementSibling;
            if (overlay && overlay.classList.contains(`${BUYO_CLASS}_popup__overlay`)) overlay.style.display = 'none';
          }
        });

        // Toggle this popup
        if (popup.style.display === 'none' || !popup.style.display) {
          // Get pricing data from the checked variant
          const checkedVariant = productCard.querySelector('.variant-container .opt-btn:checked');
          if (!checkedVariant) return;

          const priceMapping = checkedVariant.dataset.pricePerQ;
          const inventory = parseInt(checkedVariant.dataset.inventory) || 0;
          if (!priceMapping) return;

          // Parse price mapping: "1:price:compare,5:price:compare,..."
          const rawTiers = {};
          priceMapping.split(',').forEach(entry => {
            const [qty, price, comparePrice] = entry.split(':');
            rawTiers[parseInt(qty)] = { price: parseFloat(price), comparePrice: parseFloat(comparePrice) };
          });

          // Use tiers directly from the price mapping
          const tiers = Object.keys(rawTiers).map(Number).sort((a, b) => a - b).map(qty => ({
            qty,
            price: rawTiers[qty].price,
            comparePrice: rawTiers[qty].comparePrice
          }));

          // Build table rows
          const tbody = popup.querySelector('tbody');
          tbody.innerHTML = '';
          const currentQty = parseInt(buyoOption.dataset.q) || 1;

          tiers.forEach((tier, index) => {
            const isOos = tier.qty > inventory;
            const tr = document.createElement('tr');
            tr.className = 'buyo_popup__row' + (isOos ? ' is-oos' : '');
            tr.dataset.qty = tier.qty;

            const unitPrice = tier.price / tier.qty;
            const totalPrice = tier.price;

            const perPackFormatted = formatMoney(unitPrice * 100, window.money_format);
            const totalFormatted = formatMoney(totalPrice * 100, window.money_format);

            const radioId = 'buyo-popup-' + productCard.dataset.productCardId + '-' + index;
            const isSelected = tier.qty === currentQty;

            tr.innerHTML =
              '<td class="buyo_popup__td buyo_popup__td--qty"><span class="buyo_popup__qty-badge">x' + tier.qty + '</span></td>' +
              '<td class="buyo_popup__td buyo_popup__td--perpack"><span>' + perPackFormatted + '</span></td>' +
              '<td class="buyo_popup__td buyo_popup__td--total"><span>' + totalFormatted + '</span></td>' +
              '<td class="buyo_popup__td buyo_popup__td--radio">' +
                '<input type="radio" class="buyo_popup__radio" name="buyo-popup-' + productCard.dataset.productCardId + '" id="' + radioId + '" value="' + tier.qty + '"' + (isSelected ? ' checked' : '') + '>' +
                '<label class="buyo_popup__radio-label" for="' + radioId + '"></label>' +
              '</td>';

            if (isSelected) tr.classList.add('is-selected');

            if (!isOos) {
              tr.addEventListener('click', () => {
                // Update the buyo row quantity and price
                buyoOption.dataset.q = tier.qty;
                buyoOption.querySelector(`.${BUYO_CLASS}_number`).textContent = 'x' + tier.qty;

                // Update buyo_price (per-pack price for qty 1, total for others)
                const priceEl = buyoOption.querySelector(`.${BUYO_CLASS}_price`);
                if (priceEl) {
                  const bEl = priceEl.querySelector('b');
                  if (bEl) bEl.textContent = perPackFormatted;
                }

                // Update buyo_btn (total price)
                const btnEl = buyoOption.querySelector(`.${BUYO_CLASS}_btn`);
                if (btnEl) {
                  const bEl = btnEl.querySelector('b');
                  if (bEl) bEl.textContent = totalFormatted;
                }

                // Update radio selection
                popup.querySelectorAll('.buyo_popup__row').forEach(r => r.classList.remove('is-selected'));
                tr.classList.add('is-selected');
                const radio = tr.querySelector('.buyo_popup__radio');
                if (radio) radio.checked = true;

                // Close popup and overlay
                popup.style.display = 'none';
                const overlayRow = popup.previousElementSibling;
                if (overlayRow && overlayRow.classList.contains(`${BUYO_CLASS}_popup__overlay`)) overlayRow.style.display = 'none';
              });
            }

            tbody.appendChild(tr);
          });

          popup.style.display = '';
          const overlayOpen = popup.previousElementSibling;
          if (overlayOpen && overlayOpen.classList.contains(`${BUYO_CLASS}_popup__overlay`)) overlayOpen.style.display = '';
        } else {
          popup.style.display = 'none';
          const overlayClose = popup.previousElementSibling;
          if (overlayClose && overlayClose.classList.contains(`${BUYO_CLASS}_popup__overlay`)) overlayClose.style.display = 'none';
        }
      });
    });

    // Collapse button – close the popup
    document.querySelectorAll(`.${BUYO_CLASS}_popup__sort-btn`).forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const popup = btn.closest(`.${BUYO_CLASS}_popup`);
        if (popup) {
          popup.style.display = 'none';
          const overlay = popup.previousElementSibling;
          if (overlay && overlay.classList.contains(`${BUYO_CLASS}_popup__overlay`)) overlay.style.display = 'none';
        }
      });
    });

    // Close popup and overlay when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest(`.${BUYO_CLASS}_popup`) && !e.target.closest(`.${BUYO_CLASS}_left`)) {
        document.querySelectorAll(`.${BUYO_CLASS}_popup`).forEach(p => {
          p.style.display = 'none';
          const overlay = p.previousElementSibling;
          if (overlay && overlay.classList.contains(`${BUYO_CLASS}_popup__overlay`)) overlay.style.display = 'none';
        });
      }
    });
  };

  // Expose getPrices globally for other scripts
  window.getPrices = getPrices;

  document.addEventListener('DOMContentLoaded', function() {
    window.buyo_init();
  });

  document.addEventListener('on:facet-filters:updated', function() {
    buyo_init();
  });

  document.addEventListener('deferred-section:activated', function() {
    window.buyo_init();
  });

  document.addEventListener('cart:updated', function(event) {
    // Pass cart data from event to avoid fetching
    const cartData = event.detail && event.detail.cart;
    updateQuantityRestrictedButtons(cartData);
  });

  document.addEventListener('cart:refresh', function(event) {
    // Pass cart data from event to avoid fetching
    const cartData = event.detail && event.detail.cart;
    updateQuantityRestrictedButtons(cartData);
  });

  document.addEventListener('on:line-item:change', function(event) {
    // Pass cart data from event to avoid fetching
    const cartData = event.detail && event.detail.cart;
    updateQuantityRestrictedButtons(cartData);
  });
})();
